import { DestroyRef, inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, Subscription } from 'rxjs';
import { catchError, map, publishReplay, refCount } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({
  providedIn: 'root',
})
export class UserManagerService {
  private http = inject(HttpClient);

  private currentExpirySource = new BehaviorSubject<Date>(
    new Date(-8640000000000000)
  );
  currentExpiration = this.currentExpirySource.asObservable();

  private userLoggedInSource = new BehaviorSubject<boolean>(false);
  currentUserLoggedIn$ = this.userLoggedInSource.asObservable();

  private userRolesSource = new BehaviorSubject<string>('');
  currentUserRoles = this.userRolesSource.asObservable();

  private userFavoriteLocationSource = new BehaviorSubject<number>(0);
  currentUserFavoriteLocation = this.userFavoriteLocationSource.asObservable();

  $currentExpiration: Subscription;
  $logout: Subscription;
  destroyRef = inject(DestroyRef);

  public loginRequired(): boolean {
    let expiration: Date;
    this.$currentExpiration = this.currentExpiration.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      (expiry) => (expiration = expiry)
    );
    const now = new Date();
    // tslint:disable-next-line:max-line-length
    const utcNow = new Date(
      Date.UTC(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      )
    );
    if (new Date(expiration) < utcNow) {
      return true;
    } else {
      const expiry = localStorage.getItem('expiration');
      if (expiry) {
        expiration = new Date(expiry);
      }
      return expiration <= utcNow;
    }
  }

  setUserLoggedIn(): void {
    this.userLoggedInSource.next(true);
  }

  setUserLoggedOut(): void {
    localStorage.setItem('preferenceIds', '');
    localStorage.setItem('allergenIds', '');
    this.userLoggedInSource.next(false);
  }

  logOut() {
    this.userRolesSource.next('');
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    this.$logout = this.http
      .post<any>(`${environment.apiUrl}/log-out`, httpOptions)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(this.handleError))
      .subscribe((response) => {
        if (response) {
          this.setUserLoggedOut();
        }
      });
    localStorage.removeItem('expiration');
    localStorage.removeItem('userRoles');
    localStorage.removeItem('preferenceIds');
    localStorage.removeItem('allergenIds');
    localStorage.removeItem('favoriteLocationId');
  }

  checkIfUserInRole(roles: string): boolean {
    if (roles) {
      if (
        roles.includes('Admin') ||
        roles.includes('ClientManager') ||
        roles.includes('LocationManager') ||
        roles.includes('POS')
      ) {
        return true;
      }
    }
    return false;
  }

  getCurrentUserInfo(): Observable<boolean> {
    return this.http.get(`${environment.apiUrl}/get-current-user-info`).pipe(
      map((data: any) => {
        if (data) {
          this.currentExpirySource.next(data.expiration);
          localStorage.setItem('expiration', data.expiration);
          this.userRolesSource.next(data.roles);
          localStorage.setItem('userRoles', data.roles);
          localStorage.setItem(
            'preferenceIds',
            JSON.stringify(data.preferenceIds)
          );
          localStorage.setItem('allergenIds', JSON.stringify(data.allergenIds));
          this.userFavoriteLocationSource.next(data.favoriteLocationId);
          localStorage.setItem('favoriteLocationId', data.favoriteLocationId);
          this.setUserLoggedIn();
          return true;
        }
        this.setUserLoggedOut();
        return false;
      }),
      publishReplay(1),
      refCount(),
      catchError(this.handleError)
    );
  }

  saveUserFilters(
    allergenIds: number[],
    preferenceIds: number[]
  ): Observable<boolean> {
    const filterIds = allergenIds.concat(preferenceIds);
    const token = localStorage.getItem('jwt');
    const header = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      }),
    };
    const body = JSON.stringify(filterIds);
    return this.http
      .post<any>(`${environment.apiUrl}/update-user-filters`, body, header)
      .pipe(
        map((data: any) => {
          if (data === 'Saved') {
            localStorage.setItem(
              'preferenceIds',
              JSON.stringify(preferenceIds)
            );
            localStorage.setItem('allergenIds', JSON.stringify(allergenIds));
            return true;
          }
          return false;
        })
      );
  }

  updateUserLocation(locationId: number | null): Observable<boolean> {
    const token = localStorage.getItem('jwt');
    const header = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      }),
    };
    const body = JSON.stringify(locationId);
    return this.http
      .post<any>(
        `${environment.apiUrl}/update-user-favorite-location`,
        body,
        header
      )
      .pipe(
        map((data: any) => {
          if (data === 'Saved') {
            if (locationId === null) {
              localStorage.removeItem('favoriteLocationId');
            } else {
              localStorage.setItem('favoriteLocationId', String(locationId));
            }
            this.userFavoriteLocationSource.next(locationId);
            setTimeout(() => window.location.reload());
            return true;
          }
          return false;
        })
      );
  }

  private handleError(err: HttpErrorResponse) {
    let errorMessage: string;
    if (err.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      errorMessage = `Server returned code: ${err.status}, error message is: ${err.message}`;
    }
    return throwError(errorMessage);
  }
}
